<script lang="ts">
	import type { Snippet } from 'svelte';
	import CloseButton from './CloseButton.svelte';

	interface Props {
		open: boolean;
		onclose?: () => void;
		title: string;
		children: Snippet;
		footer?: Snippet;
		/** Allow closing via ESC key. Default: true */
		allowDismiss?: boolean;
		size?: 'sm' | 'md' | 'lg';
	}

	const {
		open,
		onclose,
		title,
		children,
		footer,
		allowDismiss = true,
		size = 'md'
	}: Props = $props();

	let dialog: HTMLDialogElement;
	// Several modals are mounted at once, so the heading id must be unique.
	const headingId = $props.id();

	$effect(() => {
		if (!dialog) return;
		if (open) {
			dialog.showModal();
		} else if (dialog.open) {
			dialog.close();
		}
	});

	// Cancelling keydown (not the dialog's `cancel` event, which browsers only let
	// a page prevent once per user activation) keeps non-dismissable modals open.
	function handleKeydown(e: KeyboardEvent) {
		if (!open || e.key !== 'Escape') return;
		e.preventDefault();
		if (allowDismiss) onclose?.();
	}
</script>

<svelte:window onkeydown={handleKeydown} />

<dialog
	bind:this={dialog}
	class="dialog"
	style:width="var(--width-{size})"
	aria-labelledby={headingId}
	{onclose}>
	<header>
		<h2 id={headingId}>{title}</h2>
		{#if onclose !== undefined && allowDismiss}
			<CloseButton onclick={onclose} aria-label="Close dialog" />
		{/if}
	</header>
	<div class="body">
		{@render children()}
	</div>
	{#if footer}
		<footer>{@render footer()}</footer>
	{/if}
</dialog>

<style>
	dialog.dialog {
		box-shadow: var(--shadow-lg);
		border: 1px solid var(--border-color);
		border-radius: var(--radius);
		padding: 0;
		max-width: calc(100vw - var(--space-6));

		&::backdrop {
			backdrop-filter: blur(3px);
			background: var(--backdrop);
		}

		header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			gap: var(--space-4);
			border-bottom: 1px solid var(--border-color);
			padding: var(--space-3) var(--space-3) var(--space-3) var(--space-5);

			h2 {
				font-size: var(--font-size-xl);
			}
		}

		.body {
			padding: var(--space-5);
		}

		/* Actions only: primary on the right, secondary to its left. */
		footer {
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-end;
			align-items: center;
			gap: var(--space-3);
			padding: 0 var(--space-5) var(--space-5);
		}
	}
</style>
